import { memo, useContext, useMemo } from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps } from '@mantine/core'

import WarningCircle from 'components/atoms/icons/WarningCircle'
import { AirPopulationStatus } from 'interfaces/types/AirPopulation'

import useStyles from './index.styles'
import { AirPopulationContext } from '../context'
import { ChartSizeType } from '../index.types'

export type Props = {
  size?: ChartSizeType
} & BoxProps

export const SimpleChart = memo(
  ({ size: sizeProp, styles, classNames }: Props) => {
    const { data, firstLoading, size: sizeOutside } = useContext(
      AirPopulationContext,
    )
    const size = sizeProp || sizeOutside
    const {
      color = 'gray',
      valueColorIndex = 7,
      borderEndColorIndex = 7,
      borderStartColorIndex = 3,
      currentAqiusValue = 0,
      keyCategory,
    } = data || {}

    const angleObj = useMemo(() => {
      const maxValue = 500
      const circleAngle = 360
      const gapAngleLeft = 45
      const gapAngle = gapAngleLeft * 2
      const actualCircleAngle = circleAngle - gapAngle
      const endCircleAngle = gapAngleLeft + actualCircleAngle
      const selectedDegByActualDeg = Math.min(
        ((currentAqiusValue || 0) / maxValue) * actualCircleAngle,
        actualCircleAngle,
      )
      const selectedDeg = firstLoading
        ? 120
        : selectedDegByActualDeg + gapAngleLeft
      return {
        gapAngleLeft,
        selectedDeg,
        endCircleAngle,
        circleAngle,
      }
    }, [currentAqiusValue, firstLoading])

    const { classes, cx } = useStyles(
      {
        color: firstLoading ? 'neutral' : color,
        borderEndColorIndex,
        borderStartColorIndex,
        valueColorIndex,
        firstLoading,
        size,
        ...angleObj,
        selectedDeg: angleObj.selectedDeg,
      },
      { name: 'AirPopulation__SimpleChart', styles, classNames },
    )

    return (
      <Box className={classes.root}>
        <Box className={classes.border}>
          <Box className={classes.borderWhite}>
            {!firstLoading && (
              <>
                <Text
                  size="title2"
                  variant="secondary"
                  className={cx(classes.label, 'chart-label')}
                >
                  AQI
                </Text>
                <Box className={classes.value}>{currentAqiusValue}</Box>
              </>
            )}
          </Box>
        </Box>
        {keyCategory === AirPopulationStatus.HAZARDOUS && (
          <WarningCircle className="warning-icon" color="red" size={12} />
        )}
      </Box>
    )
  },
)
